import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { ArrowRight, FileText, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeContentRow } from "@/lib/localizeContent";
import { uiT } from "@/i18n/ui";

interface CrossContentCardsProps {
  category?: string | null;
  serviceCategory?: string | null;
  industryCategory?: string | null;
  currentId: string;
  currentType: string;
  currentSlug?: string | null;
}

/**
 * Per-slug overrides: when the current post matches a key, replace the
 * auto-fetched related resources with these specific blog posts.
 */
const RELATED_BLOG_OVERRIDES: Record<string, string[]> = {
  "qbd-group-vigintake-partner-digital-transformation-safety-processes": [
    "ia-farmacovigilancia-estrategia",
    "preparacion-operativa-europa-requisitos-fv-qa-mah",
  ],
};

/**
 * Shortens a summary without cutting a word in half: trims back to the last
 * word boundary and adds an ellipsis only when text was actually removed.
 */
const truncateAtWord = (text: string, max = 120): string => {
  const clean = (text || "").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const base = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.\-–—]+$/, "");
  return `${base}…`;
};

/**
 * Shows related content from OTHER content types (e.g., a blog post shows related
 * whitepapers and case studies). This drives cross-linking and SEO authority flow.
 */
const CrossContentCards = ({ category, serviceCategory, industryCategory, currentId, currentType, currentSlug }: CrossContentCardsProps) => {
  const lang = useLanguage();
  const t = uiT(lang);

  const matchCol = serviceCategory ? "service_category" : industryCategory ? "industry_category" : category ? "category" : null;
  const matchVal = serviceCategory || industryCategory || category;
  const overrideSlugs = currentSlug ? RELATED_BLOG_OVERRIDES[currentSlug] : undefined;

  const { data: cards } = useQuery({
    queryKey: ["cross-content", matchCol, matchVal, currentId, currentType, lang, currentSlug],
    queryFn: async () => {
      const results: { title: string; summary: string; slug: string; type: string; route: string; icon: "paper" | "case" }[] = [];

      // Slug-based override: show curated blog posts instead
      if (overrideSlugs && overrideSlugs.length > 0) {
        const { data: bps } = await (supabase as any)
          .from("blog_posts")
          .select("id, title, slug, summary, lang")
          .in("slug", overrideSlugs)
          .eq("lang", lang);

        if (bps) {
          // Preserve order from overrideSlugs
          const ordered = overrideSlugs
            .map((s) => bps.find((b: any) => b.slug === s))
            .filter(Boolean);
          for (const b of ordered) {
            results.push({
              title: b.title,
              summary: truncateAtWord(b.summary),
              slug: b.slug,
              type: "blog_posts",
              route: lang === "es" ? "/es/blog" : lang === "zh-cn" ? "/zh-cn/blog" : "/en/blog",
              icon: "case",
            });
          }
        }
        return results.slice(0, 3);
      }

      if (!matchCol || !matchVal) return [];

      // Fetch related white papers
      if (currentType !== "white_papers") {
        const { data: wps } = await (supabase as any)
          .from("white_papers")
          .select("id, title, title_es, slug, slug_es, summary, summary_es, category")
          .eq(matchCol, matchVal)
          .not("published_at", "is", null)
          .order("published_at", { ascending: false })
          .limit(2);

        if (wps) {
          for (const wp of wps) {
            const localized = localizeContentRow(wp, lang);
            const esSlug = lang === "es" && wp.slug_es ? wp.slug_es : wp.slug;
            results.push({
              title: localized.title,
              summary: truncateAtWord(localized.summary),
              slug: esSlug,
              type: "white_papers",
              route: lang === "es" ? "/es/downloads" : "/en/downloads",
              icon: "paper",
            });
          }
        }
      }

      // Fetch related case studies
      if (currentType !== "case_studies") {
        const { data: cs } = await (supabase as any)
          .from("case_studies")
          .select("id, title, title_es, slug, slug_es, summary, summary_es, category")
          .eq(matchCol, matchVal)
          .not("published_at", "is", null)
          .order("published_at", { ascending: false })
          .limit(2);

        if (cs) {
          for (const c of cs) {
            const localized = localizeContentRow(c, lang);
            const esSlug = lang === "es" && c.slug_es ? c.slug_es : c.slug;
            results.push({
              title: localized.title,
              summary: truncateAtWord(localized.summary),
              slug: esSlug,
              type: "case_studies",
              route: lang === "es" ? "/es/casos-de-exito" : "/en/case-studies",
              icon: "case",
            });
          }
        }
      }

      return results.slice(0, 3);
    },
    enabled: !!(overrideSlugs || (matchCol && matchVal)),
  });


  if (!cards || cards.length === 0) return null;

  const heading = {
    en: "Related Resources",
    es: "Recursos relacionados",
    "zh-cn": "相关资源",
  };

  return (
    <div className="my-12 space-y-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
        {heading[lang] || heading.en}
      </p>
      <div className="grid gap-3">
        {cards.map((card) => (
          <Link
            key={`${card.type}-${card.slug}`}
            to={`${card.route}/${card.slug}`}
            className="group flex items-start gap-4 rounded-xl border border-border/50 bg-muted/30 p-4 hover:border-primary/30 hover:bg-muted/60 transition-all"
          >
            <div className="mt-0.5 shrink-0 rounded-lg bg-primary/10 p-2">
              {card.icon === "paper" ? (
                <FileText size={16} className="text-primary" />
              ) : (
                <BookOpen size={16} className="text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                {card.title}
              </p>
              {card.summary && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{card.summary}</p>
              )}
            </div>
            <ArrowRight size={14} className="mt-1 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default CrossContentCards;
